import { Avatar, Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import React, { useEffect, useState } from "react";
import Label from "../../Common/Label";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { getRequest, postRequest } from "../../../api/apiServices";
import apiList, { getCompany } from "../../../api/apiList";
import { useAuth } from "../../../hooks/useAuth";
import { clearCampaignPrefillStorage } from "../../../utils/campaignPrefillStorage";

interface Form7Props {
  prevStep: () => void;
}

const Form7: React.FC<Form7Props> = ({ prevStep }) => {
  const [termsChecked, setTermsChecked] = useState(true);
  const [formData, setFormData] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [companyInfoData, setCompanyInfoData] = useState<{
    companyName: string;
    companyLogo: string;
    companyId: number;
  }>({
    companyName: "",
    companyLogo: "",
    companyId: 0,
  });

  const { user } = useAuth();
  const navigate = useNavigate();

  // Load CompaignData from localStorage
  const loadCampaignData = () => {
    try {
      const stored = localStorage.getItem("CompaignData");
      if (!stored) return;

      setFormData(JSON.parse(stored) ?? {});
    } catch (error) {
      console.error("Error parsing CompaignData:", error);
    }
  };

  // Fetch company info similar to Form6
  const getCompanyData = async () => {
    try {
      const response: any = await getRequest(getCompany(user?.uId));
      if (response?.data?.data) {
        setCompanyInfoData({
          companyName: response?.data?.data?.name,
          companyLogo: response?.data?.data?.logo,
          companyId: response?.data?.data?.id,
        });
      }
    } catch (error) {
      console.error("Error in getCompanyData:", error);
    }
  };

  const buildPrettyPayload = async () => {
    // Generic clamp helper function
    const clamp = (
      value: any,
      min: number,
      max: number,
      defaultValue: number
    ): number => {
      if (value === null || value === undefined || value === "")
        return defaultValue;
      const n = Number(value);
      if (!Number.isFinite(n)) return defaultValue;
      return Math.min(Math.max(n, min), max);
    };

    // Helper function to convert to number or null
    const toNumberOrNull = (value: any) => {
      if (value === null || value === undefined || value === "") return null;
      const n = Number(value);
      if (!Number.isFinite(n) || n === 0) return null;
      return n;
    };

    // Destructure formData with defaults
    const {
      jobSpecificationId = null,
      jobVacancyTextId = null,
      vacancyTextVariant = null,
      selectedCampaigns = [],
      Form2Data: { selectedCampaigns: form2Campaigns = [] } = {},
      selectedCampaign = [],
      Form4Data: {
        recruiterFullName = "",
        recruiterEmail = "",
        contactFullName = "",
        contactEmail = "",
        contactPhone = "",
      } = {},
      Form5Data: { campaignData = {}, workingLocation = {} } = {},
      Form3Data: {
        education: edu = null,
        seniority: sen = null,
        jobFunction: jobFunc = null,
        industry: ind = null,
      } = {},
      Form1Data: {
        jobTitle: form1JobTitle = null,
        location: form1Location = null,
        jobFunction: form1JobFunc = null,
        industries: form1Industries = null,
      } = {},
    } = formData;

    // Get selected campaigns with fallback
    const campaigns = selectedCampaigns.length
      ? selectedCampaigns
      : form2Campaigns.length
      ? form2Campaigns
      : selectedCampaign;

    // Build ordered products and specs
    const orderedProducts = campaigns
      .map((c: any) => c?.productId ?? c?.id)
      .filter(Boolean);

    const orderedProductsSpecs = campaigns
      .map((c: any) => ({
        productId: c?.productId ?? c?.id,
        utm: c?.utm,
      }))
      .filter((s: any) => s.productId);

    // Early validation for ordered products
    if (!orderedProducts.length || !orderedProductsSpecs.length) {
      console.warn(
        "No channels selected. Please select at least one campaign channel before continuing."
      );
      if (typeof window !== "undefined") {
        alert(
          "No channels selected. Please select at least one campaign channel before continuing."
        );
      }
      return null;
    }

    // Use fallback for job function and industry
    const finalJobFunc = jobFunc || form1JobFunc;
    const finalIndustry = ind || form1Industries;

    // Build weekly working hours with clamp helper
    const fromWorkingHours = clamp(campaignData?.fromWorkingHours, 0, 168, 0);
    const toWorkingHours = clamp(campaignData?.toWorkingHours, 1, 168, 1);

    // Ensure 'from' is not greater than 'to'
    const weeklyWorkingHours = {
      from: fromWorkingHours,
      to: Math.max(toWorkingHours, fromWorkingHours),
    };

    // Destructure company info with defaults
    const {
      companyName = "",
      companyLogo = "",
      companyId = 0,
    } = companyInfoData;

    const payload = {
      jobSpecificationId: jobSpecificationId || null,
      jobVacancyTextId: jobVacancyTextId || null,
      vacancyTextVariant: vacancyTextVariant || null,
      form1: {
        jobTitle: form1JobTitle,
        location: form1Location,
        jobFunction: finalJobFunc || form1JobFunc,
        industries: finalIndustry || form1Industries,
      },
      campaignPayload: {
        companyId: String(companyId),
        campaignName: campaignData?.campaignName || "",
        recruiterInfo: {
          name: recruiterFullName,
          emailAddress: recruiterEmail,
        },
        postingDetails: {
          title: campaignData?.jobTitle || "",
          description: campaignData?.jobDescription || "",
          organization: {
            name: companyName,
            companyLogo: companyLogo,
          },
          contactInfo: {
            name: contactFullName,
            emailAddress: contactEmail,
            phoneNumber: contactPhone,
          },
          workingLocation: {
            addressLine1: workingLocation?.addressLine1 || "",
            addressLine2: workingLocation?.addressLine2 || "",
            postcode: workingLocation?.postCode || "",
            city: workingLocation?.city || "",
            country: workingLocation?.country || "",
            allowsRemoteWork: workingLocation?.allowsRemoteWork ? 1 : 0,
          },
          yearsOfExperience:
            toNumberOrNull(campaignData?.yearsOfExperience) || 0,
          employmentType: campaignData?.employmentType || "",
          salaryIndication: {
            period: campaignData?.period || null,
            range: {
              from: toNumberOrNull(campaignData?.salaryIndicationFrom),
              to: toNumberOrNull(campaignData?.salaryIndicationTo),
              currency: campaignData?.currency || null,
            },
          },
          weeklyWorkingHours,
        },
        targetGroup: {
          educationLevel: edu
            ? [{ description: edu?.name, vonqId: edu?.id }]
            : [],
          seniority: sen ? [{ description: sen?.name, vonqId: sen?.id }] : [],
          industry: finalIndustry
            ? [{ description: finalIndustry?.name, vonqId: finalIndustry?.id }]
            : [],
          jobCategory: finalJobFunc
            ? [{ description: finalJobFunc?.name, vonqId: finalJobFunc?.id }]
            : [],
        },
        orderedProducts,
        orderedProductsSpecs,
      },
    };

    return payload;
  };

  const handleSubmitOrderCampaign = async () => {
    try {
      setIsLoading(true);
      const payload = await buildPrettyPayload();
      if (!payload) return "";

      const response: any = await postRequest(apiList.orderCampaign, payload);
      const url = response?.data?.data?.postingDetails?.jobPageUrl;
      if (typeof url === "string" && url.trim().length > 0) {
        return url;
      }
      setErrorMessage(
        "Unable to retrieve the payment URL from server response. Please try again."
      );
      return "";
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getCompanyData();
  }, [user?.uId]);

  useEffect(() => {
    loadCampaignData();
  }, []);

  const handleContinue = async () => {
    if (!termsChecked) {
      setErrorMessage(
        "Please agree to the terms and conditions before continuing."
      );
      return;
    }

    try {
      setErrorMessage("");
      const url = await handleSubmitOrderCampaign();
      if (!url) {
        setErrorMessage(
          "Unable to retrieve the payment URL. Please try again."
        );
        return;
      }
      // window.location.href = url;
      navigate("/campaigns");
      localStorage.removeItem("CompaignData");
      clearCampaignPrefillStorage();
    } catch (error) {
      console.error("Failed to create campaign:", error);
      setErrorMessage("Failed to create campaign. Please try again.");
    }
  };

  return (
    <div className="container" id="campaign-container">
      <div className="col d-flex align-items-start justify-content-center flex-column gap-1">
        <div className="d-flex">
          <Avatar className="avatar">7</Avatar>
          <Label
            children="Almost there!"
            fontSize={"20.94px"}
            fontWeight={700}
            color="#273848"
          />
        </div>
      </div>
      <div className="col mt-4">
        {errorMessage ? (
          <div className="text-danger" style={{ marginBottom: "8px" }}>
            {errorMessage}
          </div>
        ) : null}
        <div className="user-msg">
          <p>
            The cost of the selected campaigns and channels will be
            automatically added to your monthly invoice. By continuing, you
            confirm that you have reviewed and accepted our Terms & Conditions.
          </p>
        </div>
        {/* Checkbox */}
        <div className="col mt-4">
          <div
            className="col mt-4 d-flex align-items-center justify-content-start gap-2"
            style={{ width: "fit-content" }}
          >
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsChecked}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setTermsChecked(checked);
                      if (checked) {
                        setErrorMessage("");
                      } else {
                        setErrorMessage(
                          "Please agree to the terms and conditions before continuing."
                        );
                      }
                    }}
                    sx={{
                      color: "black",
                      "&.Mui-checked": {
                        color: "black",
                      },
                    }}
                  />
                }
                label="I agree with the"
                sx={{ color: "black", marginRight: "0px" }}
              />
            </FormGroup>
            <Link
              to="/terms-conditions"
              target="_blank"
              className=" text-black"
              style={{ fontWeight: 550, fontSize: "1rem", marginTop: "-4px" }}
            >
              terms and conditions
            </Link>
          </div>
        </div>

        {/* Billing Informations */}
        <div className="col mt-4 d-flex flex-column align-items-center justify-content-center gap-2">
          <button className="continue-btn w-100" onClick={handleContinue}>
            {isLoading ? "Creating Campaign..." : "Continue"}
          </button>
          <Link
            to="/billings"
            target="_blank"
            className="text-decoration-none mt-2"
            style={{ color: "#13213B", fontWeight: 500, fontSize: "16px" }}
          >
            Billing Information & Invoice History
          </Link>
        </div>
      </div>
      <hr className="mt-5" />
      {/* Button Section */}
      <div className="col d-flex flex-column flex-sm-row align-items-center justify-content-start gap-3 pt-4">
        <button className="previous-btn col-12 col-sm-6" onClick={prevStep}>
          Previous - Order Review
        </button>
      </div>
    </div>
  );
};

export default Form7;
